import React, { useEffect, useRef, useState } from 'react';
import { buildWhatsAppUrl } from '@/lib/documents/whatsappLink';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useInvoiceTemplate } from '@/hooks/useInvoiceTemplate';
import { BookingWithFinancials } from '@/hooks/useBookingCore';
import { Send, Eye, MessageCircle, MessagesSquare, Loader2, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtime } from '@/contexts/RealtimeContext';
import {
  BookingDocumentContext,
  buildTemplateValues,
  DEFAULT_PRELIMINARY_TEMPLATE,
  DEFAULT_VOUCHER_TEMPLATE,
  renderBookingMessage,
  validateTemplate,
} from '@/lib/documents/bookingMessageTemplate';

interface WhatsAppInvoicePreviewProps {
  booking: BookingWithFinancials;
  onSend: () => void;
  initialMessageType?: 'preliminary' | 'voucher' | null;
}

export const WhatsAppInvoicePreview: React.FC<WhatsAppInvoicePreviewProps> = ({ booking, onSend, initialMessageType }) => {
  const { template, hotelName, loading: templateLoading } = useInvoiceTemplate(booking.hotel_id);
  const [preview, setPreview] = useState('');
  const [context, setContext] = useState<BookingDocumentContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [messageType, setMessageType] = useState<'preliminary' | 'voucher'>('preliminary');
  const [firstNightOverride, setFirstNightOverride] = useState('');
  const [sentMessages, setSentMessages] = useState<Set<'preliminary' | 'voucher'>>(new Set());
  const [sentDates, setSentDates] = useState<{ preliminary?: string; voucher?: string }>({});
  const initAppliedRef = useRef<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { broadcastBookingChange } = useRealtime();

  useEffect(() => {
    if (initialMessageType && initAppliedRef.current !== booking.id) {
      initAppliedRef.current = booking.id;
      setMessageType(initialMessageType);
    }

    let active = true;
    setContextLoading(true);
    supabase.rpc('get_booking_document_context', {
      p_booking_id: booking.id,
      p_hotel_id: booking.hotel_id,
    }).then(({ data, error }) => {
      if (!active) return;
      if (error || !data) {
        setContext(null);
        toast({ title: 'Документ не подготовлен', description: error?.message || 'Не получены данные брони', variant: 'destructive' });
      } else {
        setContext(data as unknown as BookingDocumentContext);
      }
      setContextLoading(false);
    });

    const dates: { preliminary?: string; voucher?: string } = {};
    const sent = new Set<'preliminary' | 'voucher'>();
    if (booking.preliminary_invoice_sent_at) {
      sent.add('preliminary');
      dates.preliminary = new Date(booking.preliminary_invoice_sent_at).toLocaleString('ru-RU');
    }
    if (booking.voucher_sent_at) {
      sent.add('voucher');
      dates.voucher = new Date(booking.voucher_sent_at).toLocaleString('ru-RU');
    }
    setSentMessages(sent);
    setSentDates(dates);

    return () => { active = false; };
  }, [booking.id, booking.hotel_id, booking.preliminary_invoice_sent_at, booking.voucher_sent_at, initialMessageType, toast]);

  const missingGuest = !booking.guest_name || booking.guest_name === 'Загрузка...' || booking.guest_name.startsWith('Гость (');
  const firstNightMissing = Boolean(context && !context.first_night_price_verified && !firstNightOverride);
  const source = messageType === 'preliminary'
    ? (template?.preliminary_template || DEFAULT_PRELIMINARY_TEMPLATE)
    : (template?.voucher_template || DEFAULT_VOUCHER_TEMPLATE);
  const templateErrors = validateTemplate(source);

  useEffect(() => {
    if (!context || missingGuest || templateErrors.length > 0) {
      setPreview('');
      return;
    }
    const override = firstNightOverride ? Number(firstNightOverride.replace(',', '.')) : null;
    const values = buildTemplateValues(context, booking.guest_name, template as unknown as Record<string, unknown>, hotelName, override);
    setPreview(renderBookingMessage(source, values));
  }, [context, missingGuest, templateErrors.length, firstNightOverride, booking.guest_name, template, hotelName, source]);

  const markPrepared = async () => {
    if (!context) return false;
    const { data, error } = await supabase.rpc('mark_booking_document_prepared', {
      p_booking_id: booking.id,
      p_hotel_id: booking.hotel_id,
      p_document_type: messageType,
      p_expected_version: context.booking_version,
    });
    if (error) {
      toast({ title: 'Канал открыт, отметка не сохранена', description: 'Бронь изменилась. Обновите документ перед повторной отправкой.', variant: 'destructive' });
      return false;
    }
    const result = data as Record<string, unknown> | null;
    if (typeof result?.booking_version === 'number') setContext(prev => prev ? { ...prev, booking_version: result.booking_version as number } : prev);
    const now = new Date().toISOString();
    setSentMessages(prev => new Set(prev).add(messageType));
    setSentDates(prev => ({ ...prev, [messageType]: new Date(now).toLocaleString('ru-RU') }));
    queryClient.invalidateQueries({ queryKey: ['optimized-bookings'], exact: false });
    broadcastBookingChange('UPDATE', booking.id);
    onSend();
    return true;
  };

  const canPrepare = Boolean(context && preview && !missingGuest && !firstNightMissing && templateErrors.length === 0 && booking.guest_phone);

  const handleWhatsApp = async () => {
    if (!canPrepare) return;
    const phone = booking.guest_phone.replace(/\D/g, '');
    window.open(buildWhatsAppUrl(phone, preview), '_blank');
    await markPrepared();
    toast({ title: 'WhatsApp открыт', description: 'Сообщение подготовлено. Доставка не подтверждена.' });
  };

  const handleTelegram = async () => {
    if (!canPrepare) return;
    await navigator.clipboard.writeText(preview);
    window.open(`https://t.me/+${booking.guest_phone.replace(/\D/g, '')}`, '_blank');
    await markPrepared();
    toast({ title: 'Текст скопирован', description: 'Telegram открыт. Вставьте и отправьте сообщение.' });
  };

  const handleMax = async () => {
    if (!canPrepare) return;
    await navigator.clipboard.writeText(preview);
    window.open(`https://max.ru/people/${booking.guest_phone.replace(/\D/g, '')}`, '_blank');
    toast({ title: 'Текст скопирован', description: 'MAX открыт. Действие не отмечено как отправка.' });
  };

  const blockingMessage = contextLoading || templateLoading
    ? 'Проверяем данные брони…'
    : missingGuest
      ? 'Ожидаем актуальное ФИО гостя из защищённого хранилища.'
      : firstNightMissing
        ? 'Для старой брони нет подтверждённой цены первых суток.'
        : templateErrors[0] || (!booking.guest_phone ? 'Телефон гостя не указан.' : '');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-2"><Eye className="h-5 w-5" />Предпросмотр сообщения</span>
            <div className="flex gap-2">
              {(['preliminary', 'voucher'] as const).map(type => (
                <div className="flex flex-col gap-1" key={type}>
                  <Button variant={messageType === type ? 'default' : 'outline'} size="sm" onClick={() => setMessageType(type)} className="relative">
                    {type === 'preliminary' ? 'Предварительный' : 'Ваучер'}
                    {sentMessages.has(type) && <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-cc-success text-[10px] font-bold text-primary-foreground">✓</span>}
                  </Button>
                  {sentDates[type] && <span className="text-xs text-muted-foreground">Подготовлен: {sentDates[type]}</span>}
                </div>
              ))}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {contextLoading || templateLoading ? (
            <div className="flex min-h-40 items-center justify-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Проверяем данные</div>
          ) : (
            <>
              {firstNightMissing && (
                <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3">
                  <div className="flex items-center gap-2 text-sm"><AlertTriangle className="h-4 w-4 text-warning" />Введите подтверждённую цену первых суток только для этого сообщения.</div>
                  <Input inputMode="decimal" value={firstNightOverride} onChange={event => setFirstNightOverride(event.target.value)} placeholder="Например, 7550" />
                </div>
              )}
              {blockingMessage && !firstNightMissing && <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">{blockingMessage}</div>}
              <Textarea value={preview} onChange={event => setPreview(event.target.value)} rows={18} disabled={!preview} aria-label="Итоговый текст сообщения" />
            </>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button onClick={handleWhatsApp} disabled={!canPrepare} size="lg"><Send className="mr-2 h-4 w-4" />WhatsApp</Button>
            <Button onClick={handleTelegram} disabled={!canPrepare} size="lg" variant="outline"><MessageCircle className="mr-2 h-4 w-4" />Telegram</Button>
            <Button onClick={handleMax} disabled={!canPrepare} size="lg" variant="outline"><MessagesSquare className="mr-2 h-4 w-4" />MAX</Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">Открытие канала означает подготовку, а не подтверждённую доставку.</p>
        </CardContent>
      </Card>
    </div>
  );
};
